import { Popover } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useClickAway } from 'ahooks'
import clsx from 'clsx'
import { CaretDownOutlined } from '@ant-design/icons'
import type { ReactNode } from 'react'
import { TooltipPlacement } from 'antd/es/tooltip'
import CustomScrollView from '@/components-mobile/ui-kit/CustomScrollView'

type ContentItemProps = {
  label: string
  value: string
  index: number
  lastIndex: number
  checkedClassName: string
  itemClassName?: string
  onClick: () => void
}
const ContentItem = ({
  label,
  index,
  lastIndex,
  itemClassName,
  checkedClassName,
  onClick,
}: ContentItemProps) => {
  return (
    <div
      className={clsx(
        'flex h-9 cursor-pointer items-center justify-between border-b border-white/10 px-4 hover:bg-white/20',
        index === 0 ? 'rounded-t-[10px]' : '',
        index === lastIndex ? 'rounded-b-[10px] border-b-0' : '',
        checkedClassName,
        itemClassName
      )}
      onClick={onClick}
    >
      <span>{label}</span>
    </div>
  )
}

type Option = {
  value: string
  label: string
}

type SelectProps = {
  options: Option[]
  value: string
  onChange: (value: string) => void
  itemClassName?: string
  className?: string
  icon?: ReactNode
  placement?: TooltipPlacement
  maxHeight?: number | string
}

export default function Select({
  options,
  itemClassName,
  className,
  value: parentValue,
  onChange,
  icon,
  placement = 'bottomRight',
  maxHeight,
}: SelectProps) {
  const ref = useRef<HTMLButtonElement>(null)
  const [value, setValue] = useState<string>(parentValue)
  const [open, setOpen] = useState(false)
  useClickAway(() => setOpen(false), ref)

  useEffect(() => {
    setValue(parentValue)
  }, [parentValue])

  const handleSelect = (v: string) => {
    setValue(v)
    setOpen(false)
    onChange(v)
  }
  const valueToLabel = useMemo(() => {
    return options.find((item) => item.value === value)?.label
  }, [options, value])

  return (
    <Popover
      open={open}
      placement={placement}
      arrow={false}
      color="transparent"
      overlayInnerStyle={{
        padding: '0',
      }}
      align={{
        offset: [0, 0],
      }}
      zIndex={1001}
      content={
        maxHeight ? (
          <div
            className="min-w-[134px] rounded-[10px] border border-white/30"
            style={{
              background:
                'linear-gradient(180deg, rgba(67, 46, 60, 1) 0%, rgba(61, 44, 41, 1) 100%)',
            }}
          >
            <CustomScrollView
              style={{
                height:
                  typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
              }}
              scrollbarTrackStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }}
              scrollbarThumbStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
              }}
            >
              {options.map((item, index) => (
                <ContentItem
                  key={item.value}
                  label={item.label}
                  value={item.value}
                  index={index}
                  lastIndex={options.length - 1}
                  onClick={() => handleSelect(item.value)}
                  itemClassName={itemClassName}
                  checkedClassName={
                    item.value === value ? 'text-white' : 'text-[#B3B3B3]'
                  }
                />
              ))}
            </CustomScrollView>
          </div>
        ) : (
          <div
            className="min-w-[134px] rounded-[10px] border border-white/30"
            style={{
              background:
                'linear-gradient(180deg, rgba(67, 46, 60, 1) 0%, rgba(61, 44, 41, 1) 100%)',
            }}
          >
            {options.map((item, index) => (
              <ContentItem
                key={item.value}
                label={item.label}
                value={item.value}
                index={index}
                lastIndex={options.length - 1}
                onClick={() => handleSelect(item.value)}
                itemClassName={itemClassName}
                checkedClassName={
                  item.value === value ? 'text-white' : 'text-[#B3B3B3]'
                }
              />
            ))}
          </div>
        )
      }
      trigger={'click'}
    >
      <button
        ref={ref}
        className={clsx(
          'flex h-9 w-auto items-center justify-between gap-[5px] rounded-full border border-tertiary-border bg-secondary-bg px-[18px] text-sm text-primary-text transition duration-500 focus:text-primary-text',
          className
        )}
        onClick={() => setOpen(!open)}
      >
        <span className="inline-block max-w-[16vw] overflow-hidden text-ellipsis whitespace-nowrap md:max-w-[20vw]">
          {valueToLabel}
        </span>
        {icon ? (
          <span className={clsx(open ? 'rotate-180' : '')}>{icon}</span>
        ) : !open ? (
          <CaretDownOutlined className={clsx('')} />
        ) : (
          <CaretDownOutlined className={clsx('rotate-180')} />
        )}
      </button>
    </Popover>
  )
}
